import { useEffect, useMemo, useState } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import type { AnyActorRef } from 'xstate';

import { usePortalLabel } from '@/hooks/usePortalLabel';

import type { TripLegEstimatedPickupContext } from '../machines/tripLegEstimatedPickup.machine';
import type {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';
import {
  isFiniteOrZero,
  hasClientPortalPickupOffsetError,
  isValidDateTime,
} from '../utils/pickerDateTime';

type PickupOffsetTrip = {
  isWillCall?: boolean;
  pickupDateTime?: unknown;
  appointmentDateTime?: unknown;
  pickupTimeZone?: string | null;
};

type PickupOffsetForm = Pick<
  UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>,
  'control' | 'getValues'
>;

const EMPTY_ESTIMATED_PICKUP_REFS: ReadonlyArray<AnyActorRef> = [];

function offsetMinutesForActors(
  estimatedPickupRefs: ReadonlyArray<AnyActorRef>,
): number[] {
  return estimatedPickupRefs.map((actor) => {
    const estimated = (
      actor.getSnapshot().context as TripLegEstimatedPickupContext
    ).estimatedPickupTime;
    return isFiniteOrZero(estimated?.totalPickupTime);
  });
}

/**
 * Changes when a leg's will-call flag, pickup timezone, or pickup/appointment
 * instant changes. The trips array itself is mutated in place, so identity
 * is not a signal.
 */
export function pickupOffsetTripKey(
  trips: ReadonlyArray<PickupOffsetTrip> | null | undefined,
): string {
  return (trips ?? [])
    .map((trip) => {
      const pickup = isValidDateTime(trip?.pickupDateTime)
        ? trip.pickupDateTime.toMillis()
        : '';
      const appointment = isValidDateTime(trip?.appointmentDateTime)
        ? trip.appointmentDateTime.toMillis()
        : '';
      return `${trip?.isWillCall ? 1 : 0}:${pickup}:${appointment}:${trip?.pickupTimeZone ?? ''}`;
    })
    .join(';');
}

/**
 * True while the client-portal pickup lead-time error would be shown.
 * The form resolver does not include this rule, so proceed buttons have to
 * read the estimated-pickup actors and the live pickup value directly.
 */
export function useClientPortalPickupOffsetBlock(
  form: PickupOffsetForm,
  estimatedPickupRefs: ReadonlyArray<AnyActorRef> | null | undefined,
): boolean {
  const isClientPortal = usePortalLabel() === 'client-portal';
  const refs = estimatedPickupRefs ?? EMPTY_ESTIMATED_PICKUP_REFS;
  const tripKey = useWatch({
    control: form.control,
    name: 'trips',
    compute: (trips) =>
      pickupOffsetTripKey(trips as ReadonlyArray<PickupOffsetTrip> | undefined),
  });
  const [estimateRevision, setEstimateRevision] = useState(0);

  useEffect(() => {
    if (!isClientPortal) return;
    const subscriptions = refs.map((actor) =>
      actor.subscribe(() => {
        setEstimateRevision((revision) => revision + 1);
      }),
    );
    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [refs, isClientPortal]);

  return useMemo(
    () =>
      hasClientPortalPickupOffsetError(
        isClientPortal,
        form.getValues('trips'),
        offsetMinutesForActors(refs),
      ),
    [estimateRevision, form, isClientPortal, refs, tripKey],
  );
}
